import type { Notification } from '@parlay-picker/shared/notification';
import type { NotificationDisplayData } from '@parlay-picker/shared/notificationDisplay';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { COLOR_SCHEME_CLASSES } from './colorScheme';
import { FallbackBody } from './FallbackBody';

/** Color dot class for settlement leg results */
const LEG_RESULT_DOT_CLASSES: Record<string, string> = {
  hit: 'bg-theme-success',
  miss: 'bg-theme-danger',
  void: 'bg-yellow-400',
  push: 'bg-gray-400',
};

/** Settlement body: summary + odds badge + DNP pills + expandable legs */
export function SettlementBody({
  displayData,
  notification,
}: {
  displayData: NotificationDisplayData;
  notification: Notification;
}): ReactElement {
  const [expanded, setExpanded] = useState(false);
  const { settlement } = displayData;
  if (!settlement) return <FallbackBody notification={notification} />;

  const colors = COLOR_SCHEME_CLASSES[displayData.colorScheme];
  const hasLegs = settlement.legs.length > 0;

  return (
    <div className="mt-2">
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-theme-text leading-snug">
            {settlement.legCount > 0
              ? `${settlement.legCount}-leg parlay ${settlement.result}`
              : notification.data.summary}
          </p>
        </div>
        {settlement.oddsFormatted && (
          <span
            className={`font-mono text-sm font-bold px-2.5 py-1 rounded-lg shrink-0 ${colors.badgeBg} ${colors.badgeText}`}
          >
            {settlement.oddsFormatted}
          </span>
        )}
      </div>
      {settlement.result === 'voided' && settlement.dnpNames.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {settlement.dnpNames.map((name) => (
            <span
              key={name}
              className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-yellow-400/15 text-yellow-400 border border-yellow-400/20"
            >
              DNP: {name}
            </span>
          ))}
        </div>
      )}
      {notification.data.details && (
        <p className="text-xs text-theme-text-tertiary mt-1.5">{notification.data.details}</p>
      )}

      {/* Expandable leg details */}
      {hasLegs && (
        <div className="mt-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((prev) => !prev);
            }}
            className="flex items-center gap-1 text-xs font-medium text-theme-text-secondary hover:text-theme-text transition-colors min-h-7"
          >
            {expanded ? (
              <>
                <ChevronUp className="w-3.5 h-3.5" />
                Hide legs
              </>
            ) : (
              <>
                <ChevronDown className="w-3.5 h-3.5" />
                Show {settlement.legs.length} legs
              </>
            )}
          </button>
          {expanded && (
            <div className="mt-1.5 space-y-1 pl-1">
              {settlement.legs.map((leg, idx) => (
                <div
                  key={`${leg.player}-${leg.statType}-${idx}`}
                  className="flex items-center gap-2 text-xs"
                >
                  <span
                    className={`w-2 h-2 rounded-full shrink-0 ${LEG_RESULT_DOT_CLASSES[leg.result] ?? 'bg-gray-400'}`}
                  />
                  <span className="font-medium text-theme-text truncate">{leg.player}</span>
                  <span className="text-theme-text-secondary truncate">
                    {[leg.overUnder, leg.line, leg.statLabel].filter(Boolean).join(' ')}
                  </span>
                  {leg.actualValue && (
                    <span className="font-mono text-theme-text-tertiary shrink-0">
                      ({leg.actualValue})
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
